import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FAQItem {
  q: string;
  a: string;
}

export default function FAQSection() {
  const { t, i18n } = useTranslation();
  const items = t('faq.items', { returnObjects: true }) as FAQItem[];

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  // Schema.org FAQPage structured data
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <section id="faq" className="py-16 md:py-24 bg-dark-950 relative overflow-hidden">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <div className="absolute inset-0 opacity-5">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-champagne-400 text-xs font-medium tracking-[0.2em] uppercase mb-3">
            FAQ
          </p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3">
            {t('faq.title')}
          </h2>
          <p className="text-white/50 text-base md:text-lg max-w-xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </div>

        <div className="space-y-3">
          {items.map((item, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className={`rounded-xl border transition-all duration-300 ${
                  isOpen
                    ? 'bg-white/5 border-champagne-500/30'
                    : 'bg-white/[0.02] border-white/10 hover:border-white/20'
                }`}
              >
                <button
                  onClick={() => toggle(idx)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 md:px-6 md:py-5 text-left cursor-pointer"
                  aria-expanded={isOpen}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                        isOpen
                          ? 'bg-champagne-500 text-dark-950'
                          : 'bg-white/10 text-champagne-400'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <span
                      className={`font-semibold text-sm md:text-base truncate ${
                        isOpen ? 'text-white' : 'text-white/80'
                      }`}
                    >
                      {item.q}
                    </span>
                  </div>
                  <i
                    className={`ri-arrow-down-s-line text-lg flex-shrink-0 transition-transform duration-300 ${
                      isOpen ? 'text-champagne-400 rotate-180' : 'text-white/40'
                    }`}
                  />
                </button>

                <div
                  className="overflow-hidden transition-all duration-300 ease-out"
                  style={{
                    maxHeight: isOpen ? '400px' : '0px',
                    opacity: isOpen ? 1 : 0,
                  }}
                >
                  <div className="px-5 pb-5 md:px-6 md:pb-6 pl-14 md:pl-16">
                    <p className="text-white/60 text-sm md:text-base leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-8 text-center">
          <p className="text-white/40 text-sm mb-3">
            {i18n.language === 'tr' ? 'Başka sorunuz mu var?' : i18n.language === 'ru' ? 'Есть другой вопрос?' : 'Have another question?'}
          </p>
          <a
            href={`https://wa.me/905313459987?text=${encodeURIComponent(
              i18n.language === 'tr'
                ? 'Merhaba Cemil Kuaför, mikro kaynak hakkında sormak istediğim bir sorum var.'
                : i18n.language === 'ru'
                  ? 'Здравствуйте, Cemil Kuaför, у меня вопрос о микро-накладках.'
                  : 'Hello Cemil Kuaför, I have a question about micro link extensions.'
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-champagne-500 text-dark-950 px-6 py-3 rounded-full font-semibold text-sm hover:bg-champagne-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-lg" />
            {t('whatsapp.cta')}
          </a>
        </div>
      </div>
    </section>
  );
}